import { APP_PATHS, ROUTE_PARAMS } from "@/constants";
import { useSupportMode } from "@/context/supportMode";
import { isActiveSupportModeForRouteAccount } from "@/context/supportMode/supportModeAuthHeaders";
import { SupportViewType, DealerUserRole } from "@/gql/graphql";
import { DEALER_ROLE_I18N_KEYS } from "@/pages/SuperAdmin/constants/supportModeConstants";
import { pageLayout } from "@/theme/pageLayout";
import { Box, Stack } from "@mui/material";
import { Button, Typography } from "@trimble/modus-cmx";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

const SupportModeBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, exitSupportMode } = useSupportMode();
  const { [ROUTE_PARAMS.ACCOUNT_ID]: accountId } = useParams();

  if (!isActiveSupportModeForRouteAccount(session, accountId)) {
    return null;
  }

  const isSalesOrderAdminMode =
    session.viewType === SupportViewType.Role &&
    session.targetRoleId === DealerUserRole.SalesOrderAdmin;

  const titleKey = isSalesOrderAdminMode
    ? "super_admin.support_mode.banner.sales_order_admin_mode"
    : "super_admin.support_mode.banner.support_admin_mode";

  let viewingMessage = "";

  if (session.viewType === SupportViewType.Role) {
    const targetRole = session.targetRoleId;
    let roleLabel: string;

    if (targetRole === DealerUserRole.SalesOrderAdmin) {
      roleLabel = t("dealer_members.roles.sales_order_admin");
    } else if (targetRole in DEALER_ROLE_I18N_KEYS) {
      roleLabel = t(DEALER_ROLE_I18N_KEYS[targetRole]);
    } else {
      roleLabel = targetRole;
    }

    viewingMessage = isSalesOrderAdminMode
      ? t("super_admin.support_mode.banner.viewing_sales_order_admin", {
          label: roleLabel,
        })
      : t("super_admin.support_mode.banner.viewing_role", {
          label: roleLabel,
        });
  } else {
    viewingMessage = t("super_admin.support_mode.banner.viewing_user", {
      label: session.targetUserDisplayName,
    });
  }

  const handleSelectAnotherAccount = () => {
    exitSupportMode();
    navigate(`/${APP_PATHS.SUPPORT_ADMIN}`);
  };

  return (
    <Stack
      sx={{
        width: "100%",
        bgcolor: "var(--modus-wc-color-base-100)",
        borderBottom: "1px solid var(--modus-wc-color-gray-1)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "flex-start" }}
        justifyContent="space-between"
        gap={2}
        sx={{
          ...pageLayout,
          py: 2,
        }}
      >
        <Stack
          direction="row"
          gap={3}
          alignItems="flex-start"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Typography
            hierarchy="h5"
            weight="bold"
            customClass="modus-wc-typography-override"
            style={{
              color: "var(--modus-wc-color-gray-6)",
              flexShrink: 0,
              lineHeight: 1.2,
              marginTop: 2,
            }}
          >
            {t(titleKey)}
          </Typography>
          <Stack direction="column" gap={0.5} sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              gap={3}
              flexWrap="wrap"
              alignItems="baseline"
              sx={{ lineHeight: 1.2 }}
            >
              <Typography
                hierarchy="h6"
                weight="bold"
                customClass="modus-wc-typography-override"
                style={{ color: "var(--modus-wc-color-gray-6)" }}
              >
                {t("super_admin.support_mode.banner.cdh_id", {
                  id: session.cdhId,
                })}
              </Typography>
              <Typography
                hierarchy="h6"
                weight="bold"
                customClass="modus-wc-typography-override"
                style={{ color: "var(--modus-wc-color-gray-6)" }}
              >
                {t("super_admin.support_mode.banner.iam_id", {
                  id: session.iamAccountId,
                })}
              </Typography>
            </Stack>
            <Typography
              hierarchy="p"
              size="md"
              weight="normal"
              customClass="modus-wc-typography-override"
              style={{ maxWidth: "100%" }}
            >
              {viewingMessage}
            </Typography>
          </Stack>
        </Stack>
        <Box sx={{ flexShrink: 0 }}>
          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            onButtonClick={handleSelectAnotherAccount}
            style={{ whiteSpace: "nowrap" }}
          >
            {t("super_admin.support_mode.banner.select_another_account")}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};

export default SupportModeBanner;
